import { FC } from "react";
import styled from "styled-components";
import { color as themeColor, Article } from "@styles";
import { H1 } from "../atoms/Heading";
import { P1, P2 } from "../atoms/Paragraph";
import { StandardPadding } from "../templates/StandardPadding";
import { Spacer } from "../templates/Spacer";
import { BlogShareIcon } from "./BlogShareIcon";
import { BlogGetAQuoteButton } from "./BlogQuoteButton";
import { getStrapiImageByBreakpoint, makeAnchorsOpenNewTab } from "@utils";
import { BlogDataQuery } from "gql";

export type ContentType = "article" | "seo-page" | "vacancy";

const giveBlockQuotesCurlyQuotationMarks = (str: string): string => {
  return (str || "")
    .replace(`<blockquote>"`, "<blockquote>“")
    .replace(`"</blockquote>`, "”</blockquote>");
};

/**
 * This function converts, for example, <ol start="3"> to <ol style="--start:3">
 * This way, we can access the --start variable within our CSS,
 * which is useful for lists which do not start at 0
 */
const addStartVariableToOrderedLists = (str: string): string => {
  return (str || "").replace(/<ol start="/g, `<ol style="--start:`);
};

/**
 * This function converts any <br> tag to a div with a height of 2rem
 * This is useful inside a <li> tag, where paragraphs may be separated by a <br>
 * as it allows writers to have multiple paragraphs within a single list item
 */
const convertBrToDiv = (str: string): string => {
  return (str || "").replace(/<br>/g, '<div style="height:2rem;"></div>');
};

export const BlogArticle: FC<
  React.PropsWithChildren<{
    postData?: BlogDataQuery["blogPosts"]["data"][number]["attributes"];
    contentType?: ContentType;
  }>
> = ({ postData, contentType = "article" }) => {
  if (!postData) {
    return null;
  }

  const { title, author, featuredImage, excerpt, post, publishDate, slug } = postData;

  let url: string;

  switch (contentType) {
    case "seo-page":
      url = slug;
      break;
    case "vacancy":
      url = `careers/${slug}`;
      break;
    case "article":
    default:
      url = `blog/${slug}`;
      break;
  }

  let html = post;
  html = giveBlockQuotesCurlyQuotationMarks(html);
  html = makeAnchorsOpenNewTab(html);
  html = addStartVariableToOrderedLists(html);
  html = convertBrToDiv(html);

  return (
    <>
      <div>
        <TitleContainer>
          <H1 align="center" fontSize="3.5rem" lineheight="4rem" pt="3rem" pb="0rem">
            {title}
          </H1>
          {contentType === "seo-page" || contentType === "vacancy" ? null : (
            <BlogExcerpt>{excerpt}</BlogExcerpt>
          )}
        </TitleContainer>
        {featuredImage?.data?.attributes?.formats ? (
          <FeatureImageWrapper>
            <FeatureImage
              src={getStrapiImageByBreakpoint(featuredImage.data.attributes.formats, "xxlarge")}
              alt=""
            />
          </FeatureImageWrapper>
        ) : (
          <Spacer height="10rem" />
        )}

        {author.data && (
          <BlogArticlePadding>
            <AuthorAndPublishedAtSection>
              {author?.data?.attributes?.image?.data?.attributes?.formats && (
                <BlogAvatarContainer>
                  <BlogAvatar
                    src={getStrapiImageByBreakpoint(
                      author.data.attributes.image.data.attributes.formats,
                      "medium",
                    )}
                  />
                </BlogAvatarContainer>
              )}
              <P2 color="#A0A09B" style={{ marginRight: "1.5rem" }}>
                {author.data.attributes.name} -{" "}
                {new Date(publishDate).toLocaleString("default", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </P2>
              <BlogShareIcon title={title} slug={slug} />
            </AuthorAndPublishedAtSection>
          </BlogArticlePadding>
        )}

        <Spacer height="3rem" />

        <Article dangerouslySetInnerHTML={{ __html: html }} />

        <BlogArticlePadding>
          <BlogShareIcon title={title} slug={slug} textVersion contentType={contentType} />
          <BlogGetAQuoteButton />
        </BlogArticlePadding>

        {author.data ? (
          <BlogArticlePadding>
            <BlogAuthorCard>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "0.75rem" }}>
                {author?.data?.attributes?.image?.data?.attributes?.formats && (
                  <BlogAvatar
                    src={getStrapiImageByBreakpoint(
                      author.data.attributes.image.data.attributes.formats,
                      "medium",
                    )}
                  />
                )}
                <Strong>{author.data.attributes.name}</Strong>
              </div>
              <P1>{author.data.attributes.bio}</P1>
            </BlogAuthorCard>
          </BlogArticlePadding>
        ) : (
          <Spacer height="5rem" />
        )}
      </div>
    </>
  );
};

const FeatureImageWrapper = styled.div`
  width: 100%;
  max-width: 70rem;
  height: auto;
  max-height: 29.5rem;
  margin: 0 auto 3rem auto;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0.5rem 0.5rem 0 #f5f5f5;
  border-radius: 0.5rem;

  img {
    border-radius: 0.5rem;
  }

  @media only screen and (max-width: 70rem) {
    border-radius: 0;

    img {
      border-radius: 0;
    }
  }

  margin-top: 3.5rem;

  @media only screen and (max-width: 65rem) {
    margin-top: 5rem;
  }

  @media only screen and (max-width: 30rem) {
    margin-top: 2.5rem;
  }
`;

const FeatureImage = styled.img`
  width: 100%;
  max-width: 70rem;
  object-fit: cover;
  border-radius: 0.5rem;
  height: auto;
  max-height: 29.5rem;
  border: 0.125rem solid #e3e3e1;
  pointer-events: none;

  @media only screen and (max-width: 70rem) {
    border-radius: 0;
  }
`;

const BlogArticlePadding = styled(StandardPadding)`
  max-width: 50rem;
  margin: 0 auto;
`;

const TitleContainer = styled(StandardPadding)`
  max-width: 62rem;
  margin: 0 auto;
`;

const customTextSelection = `
  ::-moz-selection {
    background: ${themeColor.P20};
  }
  ::selection {
    background: ${themeColor.P20};
  }
`;

const BlogExcerpt = styled(P1)`
  font-weight: bold;
  font-size: 1.25rem;
  line-height: 2rem;
  text-align: center;
  letter-spacing: 0.075rem;
  color: #5c5757;
  ${customTextSelection};
`;

const AuthorAndPublishedAtSection = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0;
  justify-content: center;
  text-align: center;
`;

const BlogAvatarContainer = styled.div`
  transform: translate(-0.66rem, 0.66rem);
`;

const BlogAuthorCard = styled.div`
  padding: 2rem;
  background: #fafafe;
  border: 0.125rem solid #e3e3e1;
  box-sizing: border-box;
  box-shadow: 0.5rem 0.5rem 0 #f5f5f5;
  border-radius: 0.5rem;
  margin: 3rem 0 8.5rem 0;
  transition: 200ms ease-out;

  &:hover {
    transform: translateY(-0.25rem);
  }
`;

const BlogAvatar = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  margin-right: 1rem;
`;

const Strong = styled.strong`
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 1.2px;
  color: #640038;
  ${customTextSelection};
`;
